/**
 * Polygon mode overlay for model geometries.
 *
 * When polygonMode is true this renders WireframeGeometry line segments
 * on top of the solid model, showing every triangle edge (including interior
 * face diagonals). Unlike WireframeOverlay (which uses EdgesGeometry and only
 * draws edges at sharp corners), this shows the full triangulated mesh.
 *
 * The solid model remains fully visible underneath — this is a pure overlay.
 */

import { useMemo } from 'react';
import * as THREE from 'three';
import type { ColoredGeometry } from '@/components/viewer/types';

function PolygonLines({
  geometry,
  color = 'white',
  opacity = 0.4,
}: {
  geometry: THREE.BufferGeometry;
  color?: string;
  opacity?: number;
}) {
  const wireframeGeometry = useMemo(() => {
    return new THREE.WireframeGeometry(geometry);
  }, [geometry]);

  const lineMaterial = useMemo(() => {
    return new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity,
    });
  }, [color, opacity]);

  const lineSegments = useMemo(() => {
    return new THREE.LineSegments(wireframeGeometry, lineMaterial);
  }, [wireframeGeometry, lineMaterial]);

  return <primitive object={lineSegments} />;
}

export function PolygonOverlay({
  geometry,
  coloredGeometries,
  polygonMode,
}: {
  geometry?: THREE.BufferGeometry;
  coloredGeometries: ColoredGeometry[];
  polygonMode: boolean;
}) {
  if (!polygonMode) return null;

  return (
    <>
      {coloredGeometries.length > 0 ? (
        <group rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
          {coloredGeometries.map((cg) => (
            <PolygonLines key={cg.geometry.uuid} geometry={cg.geometry} />
          ))}
        </group>
      ) : geometry ? (
        <group rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
          <PolygonLines geometry={geometry} />
        </group>
      ) : null}
    </>
  );
}
